/**
 * Varsler Fase 2: personlig side der hver bruker styrer hvert varseltype til
 * Av / Kun i appen / App + e-post. Top-level (ikke under /innstillinger som
 * er admin-gated) siden personlige preferanser gjelder alle roller.
 *
 * Tier-synlighet: Personlig = alle; Prosjekt = alle utenom external;
 * Ledelse = admin/superadmin.
 */
import { useMemo } from "react";
import { SrIcon } from "@/components/ui/SrIcon";
import { NotificationTierSection } from "@/components/settings/NotificationTierSection";
import { useRBAC } from "@/hooks/use-rbac";
import {
  NOTIFICATION_TYPES,
  type NotificationLevel,
  type NotificationTier,
} from "@/lib/notification-catalog";
import { type NotificationType } from "@/hooks/use-notifications";
import {
  useNotificationPreferences,
  useSetNotificationPreference,
} from "@/hooks/use-notification-preferences";

export default function VarslerPage() {
  const { role, isAdmin } = useRBAC();
  const { data: prefs, isLoading } = useNotificationPreferences();
  const setPreference = useSetNotificationPreference();

  // Hvilke tiers brukeren kan motta (og dermed ser i UI).
  const visibleTiers = useMemo<NotificationTier[]>(() => {
    const tiers: NotificationTier[] = ["personal"];
    if (role !== "external") tiers.push("project");
    if (isAdmin) tiers.push("leadership");
    return tiers;
  }, [role, isAdmin]);

  // Aktivt nivå per type: pref-rad hvis satt, ellers katalog-default.
  const levelByType = useMemo(() => {
    const byType = {} as Record<NotificationType, NotificationLevel>;
    const prefMap = new Map(prefs?.map((p) => [p.type, p.level]) ?? []);
    for (const type of Object.keys(NOTIFICATION_TYPES) as NotificationType[]) {
      byType[type] = prefMap.get(type) ?? NOTIFICATION_TYPES[type].defaultLevel;
    }
    return byType;
  }, [prefs]);

  const handleChange = (type: NotificationType, level: NotificationLevel) => {
    setPreference.mutate({ type, level });
  };

  return (
    <div className="page-container animate-fade-in space-y-6">
      <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2 mb-2">
        <SrIcon name="bell" className="h-6 w-6" />
        Varsler
      </h1>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Styr hvordan du vil bli varslet for hver hendelsestype.
      </p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster preferanser…</p>
      ) : (
        <div className="space-y-6">
          {visibleTiers.map((tier) => (
            <NotificationTierSection
              key={tier}
              tier={tier}
              levelByType={levelByType}
              onChange={handleChange}
              disabled={setPreference.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}
